import { useQuery, useMutation } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import { schedulingApi } from "../api/scheduling";
import dayjs, { Dayjs } from "dayjs";
import { formatApiStartDate, formatApiEndDate } from "../../../shared/utils";

export interface OutageWindowData {
  id: number;
  project_id: number;
  outage_start_date: string;
  outage_end_date: string;
  status?: string;
  status_display_text: string;
}

export interface OutageWindowForm {
  id?: number;
  outageStartDate: Dayjs | null;
  outageEndDate: Dayjs | null;
  status?: string;
}

export interface UseOutageWindowDataProps {
  projectId: string | undefined;
}

export interface DateValidationErrors {
  startDate?: string;
  endDate?: string;
}

export const useOutageWindowData = ({
  projectId,
}: UseOutageWindowDataProps) => {
  const [newOutageWindows, setNewOutageWindows] = useState<OutageWindowForm[]>(
    [],
  );
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingWindowId, setEditingWindowId] = useState<number | null>(null);
  const [editWindow, setEditWindow] = useState<OutageWindowForm | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    Record<number | string, DateValidationErrors>
  >({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { data, isFetching, isLoading: isQueryLoading, isRefetching, refetch } = useQuery<
    OutageWindowData[]
  >({
    queryKey: ["outage-window", projectId],
    queryFn: () => schedulingApi.getOutageWindows(projectId),
    enabled: !!projectId,
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: async (windows: OutageWindowForm[]) => {
      if (!projectId) throw new Error("Project ID is required");

      const payload = windows.map((window) => ({
        outage_start_date: formatApiStartDate(window.outageStartDate),
        outage_end_date: formatApiEndDate(window.outageEndDate),
      }));

      return schedulingApi.createOutageWindow(projectId, payload);
    },
    onSuccess: (response: any) => {
      setSuccessMessage(
        response?.message || "Outage windows created successfully",
      );
      setNewOutageWindows([]);
      setSubmitError(null);
      refetch();
    },
    onError: (error: any) => {
      setSubmitError(
        error?.response?.data?.message || "Failed to create outage windows",
      );
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (payload: {
      windowId: number;
      data: OutageWindowForm;
    }) => {
      if (!projectId) throw new Error("Project ID is required");

      const formatPayload = {
        outage_start_date: formatApiStartDate(payload.data.outageStartDate),
        outage_end_date: formatApiEndDate(payload.data.outageEndDate),
      };

      return schedulingApi.updateOutageWindow(
        projectId,
        payload.windowId,
        formatPayload,
      );
    },
    onSuccess: (response: any) => {
      setSuccessMessage(
        response?.message || "Outage window updated successfully",
      );
      setIsEditing(false);
      setEditWindow(null);
      setEditingWindowId(null);
      setSubmitError(null);
      refetch();
    },
    onError: (error: any) => {
      setSubmitError(
        error?.response?.data?.message || "Failed to update outage window",
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (windowId: number) => {
      if (!projectId) throw new Error("Project ID is required");
      return schedulingApi.deleteOutageWindow(projectId, windowId);
    },
    onSuccess: (response: any) => {
      setSuccessMessage(
        response?.message || "Outage window deleted successfully",
      );
      setSubmitError(null);
      refetch();
    },
    onError: (error: any) => {
      setSubmitError(
        error?.response?.data?.message || "Failed to delete outage window",
      );
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async (payload: { windowId: number; status: string }) => {
      if (!projectId) throw new Error("Project ID is required");
      return schedulingApi.updateOutageWindow(projectId, payload.windowId, {
        status: payload.status,
      });
    },
    onSuccess: (response: any) => {
      setSuccessMessage(response?.message || "Status updated successfully");
      setSubmitError(null);
      refetch();
    },
    onError: (error: any) => {
      setSubmitError(
        error?.response?.data?.message || "Failed to update status",
      );
    },
  });

  // Validation logic
  const validateDateRange = (
    startDate: Dayjs | null,
    endDate: Dayjs | null,
    id?: number | string,
  ): DateValidationErrors => {
    const errors: DateValidationErrors = {};

    if (!startDate) {
      errors.startDate = "Start date is required";
    }

    if (!endDate) {
      errors.endDate = "End date is required";
    }

    if (startDate && endDate) {
      if (endDate.isBefore(dayjs().startOf("day"))) {
        errors.endDate = "End date must be in the future";
      }

      if (!endDate.isAfter(startDate)) {
        errors.endDate = "End date must be after start date";
      }
    }

    return errors;
  };

  // Add new outage window form
  const handleAddOutageWindow = () => {
    const newId = `new-${Date.now()}`;
    setNewOutageWindows((prev) => [
      ...prev,
      {
        id: newId as any,
        outageStartDate: null,
        outageEndDate: null,
        status: "active",
      },
    ]);
  };

  // Handle removing new form entry
  const handleRemoveNewOutageWindow = (id: number | string) => {
    setNewOutageWindows((prev) => prev.filter((ow) => ow.id !== id));
    setValidationErrors((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };

  // Handle field changes for new entries
  const handleChangeNewWindow = (
    id: number | string,
    field: "startDate" | "endDate",
    value: Dayjs | null,
  ) => {
    setNewOutageWindows((prev) =>
      prev.map((ow) =>
        ow.id === id
          ? {
              ...ow,
              outageStartDate:
                field === "startDate" ? value : ow.outageStartDate,
              outageEndDate: field === "endDate" ? value : ow.outageEndDate,
            }
          : ow,
      ),
    );

    // Clear error for this field when user starts typing
    setValidationErrors((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field === "startDate" ? "startDate" : "endDate"]: undefined,
      },
    }));
  };

  // Handle field changes for edit mode
  const handleChangeEditWindow = (
    field: "startDate" | "endDate",
    value: Dayjs | null,
  ) => {
    setEditWindow((prev) =>
      prev
        ? {
            ...prev,
            outageStartDate:
              field === "startDate" ? value : prev.outageStartDate,
            outageEndDate: field === "endDate" ? value : prev.outageEndDate,
          }
        : prev,
    );

    setValidationErrors((prev) => ({
      ...prev,
      edit: {
        ...prev.edit,
        [field === "startDate" ? "startDate" : "endDate"]: undefined,
      },
    }));
  };

  // Enable edit mode
  const handleEnableEdit = (id: number) => {
    if (!data) return;

    const selected = data.find((ow) => ow.id === id);
    if (!selected) return;

    setIsEditing(true);
    setEditingWindowId(id);
    setValidationErrors({});
    setSubmitError(null);
    setSuccessMessage(null);

    setEditWindow({
      id: selected.id,
      outageStartDate: dayjs(selected.outage_start_date),
      outageEndDate: dayjs(selected.outage_end_date),
      status: selected.status || "active",
    });
  };

  // Disable edit mode
  const handleDisableEdit = () => {
    setIsEditing(false);
    setEditWindow(null);
    setEditingWindowId(null);
    setValidationErrors({});
  };

  // Submit new outage windows
  const handleSubmitNewWindows = async () => {
    const errors: Record<number | string, DateValidationErrors> = {};
    let hasErrors = false;

    newOutageWindows.forEach((window) => {
      const windowErrors = validateDateRange(
        window.outageStartDate,
        window.outageEndDate,
        window.id,
      );
      if (Object.keys(windowErrors).length > 0) {
        errors[window.id!] = windowErrors;
        hasErrors = true;
      }
    });

    if (hasErrors) {
      setValidationErrors(errors);
      return;
    }

    setValidationErrors({});
    await createMutation.mutateAsync(newOutageWindows);
  };

  // Submit edit
  const handleSubmitEdit = async () => {
    if (!editWindow || editingWindowId === null) return;

    const errors = validateDateRange(
      editWindow.outageStartDate,
      editWindow.outageEndDate,
      "edit",
    );
    if (Object.keys(errors).length > 0) {
      setValidationErrors({ edit: errors });
      return;
    }

    setValidationErrors({});
    await updateMutation.mutateAsync({
      windowId: editingWindowId,
      data: editWindow,
    });
  };

  // Delete outage window
  const handleDeleteOutageWindow = async (id: number) => {
    if (!id) return;
    await deleteMutation.mutateAsync(id);
  };

  // Deactivate (change status to inactive)
  const handleDeactivateWindow = async (id: number) => {
    await updateStatusMutation.mutateAsync({
      windowId: id,
      status: "in_active",
    });
  };

  // Activate (change status to active)
  const handleActivateWindow = async (id: number) => {
    await updateStatusMutation.mutateAsync({
      windowId: id,
      status: "active",
    });
  };

  // Clear messages
  const clearMessages = () => {
    setSubmitError(null);
    setSuccessMessage(null);
  };

  // Sort outage windows by start date - memoized to prevent infinite loops
  const sortedOutageWindows = useMemo(() => {
    return (data || []).sort((a, b) => {
      const dateA = dayjs(a.outage_start_date);
      const dateB = dayjs(b.outage_start_date);
      return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
    });
  }, [data]);

  return {
    // Data
    outageWindowsData: sortedOutageWindows,
    isLoading: isQueryLoading || isFetching || isRefetching,

    // New windows state
    newOutageWindows,
    setNewOutageWindows,

    // Edit mode state
    isEditing,
    editingWindowId,
    editWindow,

    // Validation and messages
    validationErrors,
    submitError,
    successMessage,
    clearMessages,

    // Handlers for new windows
    handleAddOutageWindow,
    handleRemoveNewOutageWindow,
    handleChangeNewWindow,
    handleSubmitNewWindows,

    // Handlers for edit mode
    handleEnableEdit,
    handleDisableEdit,
    handleChangeEditWindow,
    handleSubmitEdit,

    // Handlers for delete and status
    handleDeleteOutageWindow,
    handleDeactivateWindow,
    handleActivateWindow,

    // Validation
    validateDateRange,

    // Refetch
    refetch,
  };
};
